import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import MOBILE from "../mobileConfig";
import { useMobileQuery } from "../useIsMobile";

/**
 * Long text on a record card: cut at READ_MORE_LINES lines, with "Read more" /
 * "Show less" under it (Step 81, `MOBILE.readMore`).
 *
 * Desktop clamps the same text to 2-4 lines and reveals the rest on hover.
 * Mobile took that clamp off (no hover to reveal with), so the full text ran
 * on — and a 250-word description is several screens of one card. This puts a
 * clamp back with a reveal a finger can reach.
 *
 * Mounted by the three components that draw long text — MetaTooltip,
 * BulletListCell and NoteCard — each behind `useMobileReadMore()`, so their
 * desktop returns are untouched. Also by the appointment sheet's Reason
 * (MobileApptSheet, Step 85), which is mobile code with no desktop return.
 *
 * THE BUTTON SHOWS ONLY WHEN THE TEXT IS REALLY CUT (scrollHeight beyond
 * clientHeight, MetaTooltip's own test). Measured after every render and again
 * whenever the box resizes: rotation, a font arriving, and a tab first laid
 * out while hidden all change the answer without a render.
 */

/**
 * The gate, said once. `useMobileQuery` rather than useIsMobile: no root-class
 * side effect, and right on the first render after crossing the breakpoint.
 * `patientScreen` too, so that flag off still means the plain desktop tree.
 */
export const useMobileReadMore = () => {
  const mobile = useMobileQuery();
  return mobile && MOBILE.patientScreen && MOBILE.readMore;
};

/** Show less in a sheet: px kept above the text, for its field's label. */
const SHEET_LABEL_ROOM = 24;

const MobileReadMore = ({ children, lines, textClassName }) => {
  const n = lines || MOBILE.READ_MORE_LINES;
  const [expanded, setExpanded] = useState(false);
  const [cut, setCut] = useState(false);
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const collapsing = useRef(false);

  /* While open there is nothing to measure: the button stays, as Show less. */
  const measure = useCallback(() => {
    const el = textRef.current;
    if (!el || expandedRef.current) return;
    setCut(el.scrollHeight - el.clientHeight > 1);
  }, []);

  useLayoutEffect(() => {
    measure();
    /* SHOW LESS CAN STRAND THE READER. A long text read to its end and closed
       shrinks by twenty lines above the button, so the card's top is now off
       screen and the page shows whatever came next. Bring the card back —
       `scroll-margin-top` (mobile.scss) keeps it below the pinned rows. */
    if (collapsing.current) {
      collapsing.current = false;
      const wrap = wrapRef.current;
      /* In a bottom sheet (the appointment sheet's Reason) the body is what
         scrolls, and its top edge, not the screen's, is where text goes out of
         sight. Scroll that body alone — scrollIntoView would also move the
         locked page behind the sheet — leaving room for the field's label. */
      const sheet = wrap && wrap.closest ? wrap.closest(".zym-sheet-body") : null;
      if (sheet) {
        const above = wrap.getBoundingClientRect().top - sheet.getBoundingClientRect().top;
        if (above < 0) sheet.scrollTop += above - SHEET_LABEL_ROOM;
      } else if (wrap && wrap.getBoundingClientRect().top < 0 && wrap.scrollIntoView) {
        wrap.scrollIntoView({ block: "start" });
      }
    }
  });

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <span ref={wrapRef} className="zym-more">
      <span
        ref={textRef}
        className={`zym-more-text${textClassName ? ` ${textClassName}` : ""}${expanded ? " is-open" : ""}`}
        style={expanded ? undefined : { WebkitLineClamp: n }}
      >
        {children}
      </span>
      {(cut || expanded) && (
        <button
          type="button"
          className="zym-more-btn"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            if (expanded) collapsing.current = true;
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </span>
  );
};

MobileReadMore.propTypes = {
  children: PropTypes.node,
  /** Override READ_MORE_LINES. */
  lines: PropTypes.number,
  /** The host's own text class, for its type and colour — e.g. MetaTooltip's
      `med3-meta-clamp`. The clamp itself stays this component's. */
  textClassName: PropTypes.string,
};

export default MobileReadMore;
